import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import AppShell from "../../../components/layout/AppShell";
import StatCard from "../../../components/ui/StatCard";
import EmptyState from "../../../components/ui/EmptyState";
import Pagination from "../../../components/ui/Pagination";
import { getSenderInvoices } from "../../../services/api";
import InvoiceDocLink from "../../../components/ui/InvoiceDocLink";
import { formatAmount, getMaxDecimalsForCurrency } from "../../../utils";

// ─── Constants ────────────────────────────────────────────────────────────────
const TABS = ["All", "Paid", "In Progress", "Pending"];
const FE_PAGE_SIZE = 10;
const API_LIMIT = 50;

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmt = (n) =>
  `USD ${formatAmount(n,getMaxDecimalsForCurrency("USD"))}`;

const formatDate = (iso) =>
  iso
    ? new Date(iso).toLocaleDateString("en-GB", {
        day: "2-digit",
        month: "short",
        year: "numeric",
      })
    : "—";

const statusLabel = (s) => {
  if (s === "success" || s === "fulfilled") return "Paid";
  if (s === "processing" || s === "quote_locked") return "In Progress";
  return "Pending";
};

const statusColor = (s) => {
  if (s === "success" || s === "fulfilled") return "text-green-600";
  if (s === "processing" || s === "quote_locked") return "text-blue-600";
  return "text-orange-500";
};

const kybBadgeClass = (s) =>
  s === "APPROVED"
    ? "bg-green-50 text-green-700 border border-green-200"
    : s === "REJECTED"
    ? "bg-red-50 text-red-700 border border-red-200"
    : "bg-orange-50 text-orange-700 border border-orange-200";

const kybLabel = (s) =>
  s === "APPROVED" ? "KYB Approved" : s === "REJECTED" ? "KYB Rejected" : "KYB Pending";

// ─── Component ────────────────────────────────────────────────────────────────
const SenderDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const location = useLocation();

  // Sender metadata passed from the list page via navigation state
  const sender = location.state?.sender || null;

  // Invoices state
  const [invoices, setInvoices] = useState([]);
  const [totalFromApi, setTotalFromApi] = useState(0);
  const [loading, setLoading] = useState(true);
  const [apiPage, setApiPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // FE pagination & filter
  const [fePage, setFePage] = useState(1);
  const [filter, setFilter] = useState("All");

  // Fetch invoices from API (batch of API_LIMIT)
  useEffect(() => {
    if (!id) return;
    setLoading(true);
    getSenderInvoices(id, { page: apiPage, limit: API_LIMIT })
      .then((res) => {
        const data = res.data || [];
        setInvoices(data);
        setTotalFromApi(res.meta?.total || data.length);
        setHasMore(res.meta?.totalPages > apiPage);
      })
      .catch(() => setInvoices([]))
      .finally(() => setLoading(false));
  }, [id, apiPage]);

  // Reset FE page when filter changes
  useEffect(() => {
    setFePage(1);
  }, [filter]);

  // ── Aggregates (over all fetched invoices) ───────────────────────────────
  const totalBilled = useMemo(
    () => invoices.reduce((s, i) => s + Number(i.amount || 0), 0),
    [invoices]
  );
  const settled = useMemo(
    () =>
      invoices
        .filter((i) => statusLabel(i.status) === "Paid")
        .reduce((s, i) => s + Number(i.amount || 0), 0),
    [invoices]
  );

  // ── Filter & FE pagination ───────────────────────────────────────────────
  const filtered = useMemo(
    () =>
      invoices.filter((i) => {
        if (filter === "All") return true;
        return statusLabel(i.status) === filter;
      }),
    [invoices, filter]
  );

  const totalFePages = Math.max(1, Math.ceil(filtered.length / FE_PAGE_SIZE));
  const displayed = filtered.slice((fePage - 1) * FE_PAGE_SIZE, fePage * FE_PAGE_SIZE);

  return (
    <AppShell>
      {/* ── Sender header card ─────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl px-6 py-5 mb-6 flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-bold text-gray-900 mb-1">
            {sender?.name || "Sender"}
          </h1>
          {(sender?.email || sender?.phone) && (
            <p className="text-sm text-gray-500 mb-2">
              {[sender.email, sender.phone].filter(Boolean).join(" · ")}
            </p>
          )}
          {sender?.status && (
            <span
              className={`inline-block text-xs font-semibold px-2.5 py-1 rounded-full ${kybBadgeClass(
                sender.status
              )}`}
            >
              {kybLabel(sender.status)}
            </span>
          )}
        </div>
        <button
          onClick={() =>
            navigate("/receiver/invoices/create", {
              state: { prefillSender: sender },
            })
          }
          className="flex-shrink-0 bg-brand text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-brandHover transition-colors"
        >
          Create Invoice
        </button>
      </div>

      {/* ── Stat cards ────────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-4 mb-6">
        <StatCard
          label="Total Invoices"
          value={loading ? "—" : totalFromApi.toString()}
          subLabel="All time"
        />
        <StatCard
          label="Total Billed"
          value={loading ? "—" : fmt(totalBilled)}
          subLabel="USD"
        />
        <StatCard
          label="Paid"
          value={loading ? "—" : fmt(settled)}
          subLabel="Paid out"
          valueColor="text-blue-600"
        />
      </div>

      {/* ── Invoice table card ─────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl">
        <div className="px-5 py-4 border-b border-gray-100">
          <h2 className="text-base font-semibold text-gray-900">
            Invoices from {sender?.name || "this Sender"}
          </h2>
        </div>

        {/* Filter tabs */}
        <div className="flex items-center gap-1 px-5 py-3 border-b border-gray-100">
          {TABS.map((t) => (
            <button
              key={t}
              onClick={() => setFilter(t)}
              className={`px-3 py-1.5 text-sm rounded-full border transition-colors ${
                filter === t
                  ? "bg-brand text-white border-brand"
                  : "bg-white text-gray-600 border-gray-300 hover:border-gray-400"
              }`}
            >
              {t}
            </button>
          ))}
        </div>

        {/* Table */}
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                {["Invoice ID", "Description", "Amount", "Raised Date", "Due Date", "Status", "Document", ""].map(
                  (h) => (
                    <th
                      key={h}
                      className="px-4 py-3 text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider"
                    >
                      {h}
                    </th>
                  )
                )}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                [...Array(4)].map((_, i) => (
                  <tr key={i} className="border-t border-gray-100 animate-pulse">
                    {[...Array(8)].map((__, j) => (
                      <td key={j} className="px-4 py-3">
                        <div className="h-4 bg-gray-100 rounded w-20" />
                      </td>
                    ))}
                  </tr>
                ))
              ) : displayed.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <EmptyState
                      heading="No invoices found"
                      subtext={
                        filter === "All"
                          ? "No invoices have been raised for this sender yet."
                          : `No ${filter.toLowerCase()} invoices found.`
                      }
                    />
                  </td>
                </tr>
              ) : (
                displayed.map((inv) => (
                  <tr
                    key={inv.id}
                    className="border-t border-gray-100 hover:bg-gray-50 transition-colors"
                  >
                    <td className="px-4 py-3 text-sm text-gray-900">
                      {inv.invoiceId || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 max-w-[180px] truncate">
                      {inv.comment || "—"}
                    </td>
                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                      {fmt(inv.amount)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                      {formatDate(inv.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500 whitespace-nowrap">
                      {inv.dueDate ? formatDate(inv.dueDate) : "—"}
                    </td>
                    <td
                      className={`px-4 py-3 text-sm font-medium ${statusColor(
                        inv.status
                      )}`}
                    >
                      {statusLabel(inv.status)}
                    </td>
                    <td className="px-4 py-3">
                      <InvoiceDocLink invoiceId={inv.id} docUrl={inv.docUrl} />
                    </td>
                    <td className="px-4 py-3 text-right">
                      <button
                        onClick={() => navigate(`/receiver/invoices/${inv.id}`)}
                        className="text-sm text-brand hover:underline whitespace-nowrap"
                      >
                        View →
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        <Pagination page={fePage} totalPages={totalFePages} onPageChange={setFePage} />
      </div>
    </AppShell>
  );
};

export default SenderDetail;
